import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess, refreshToken } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";
import { notification } from "antd";

const unauthorizedCode = [401, 403];

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 180000,
});

// Config
const TOKEN_PAYLOAD_KEY = "Authorization";

// Variável para controlar a renovação de token (evita múltiplas chamadas simultâneas)
let isRefreshing = false;
let failedQueue = [];

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    // Do something with request error here
    notification.error({
      message: "Error",
    });
    Promise.reject(error);
  }
);

// API respone interceptor
service.interceptors.response.use(
  (response) => {
    if (response.data instanceof Blob) {
      const contentType = response.headers["content-type"];
      const disposition = response.headers["content-disposition"];

      if (contentType.includes("application/pdf")) {
        const fileURL = URL.createObjectURL(response.data);
        window.open(fileURL, "_blank");
        return response.data;
      }

      let filename = "unknown";

      if (disposition && disposition.indexOf("filename=") !== -1) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match.length === 2) filename = match[1];
      }
      const href = URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    }
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    let notificationParam = {
      message: "",
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Não tentar renovar se a requisição já é de refresh token
      if (originalRequest.url?.includes("/refresh")) {
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        store.dispatch(signOutSuccess());
        notificationParam.message = "Falha na Autenticação";
        notificationParam.description = "Por favor, faça login novamente";
        notification.error(notificationParam);
        return Promise.reject(error);
      }

      // Se já existe uma renovação em andamento, aguardar o resultado
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${token}`;
            return service(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Iniciar renovação do token
      isRefreshing = true;

      try {
        const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
        if (refreshTokenValue) {
          const result = await store.dispatch(refreshToken());

          if (result.meta?.requestStatus === "fulfilled") {
            const newToken = result.payload.token;

            // Processar todas as requisições na fila com o novo token
            failedQueue.forEach((prom) => {
              prom.resolve(newToken);
            });
            failedQueue = [];
            isRefreshing = false;

            // Repetir a requisição original
            originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${newToken}`;
            return service(originalRequest);
          } else {
            // Falha na renovação - rejeitar todas as requisições na fila
            failedQueue.forEach((prom) => {
              prom.reject(new Error("Falha ao renovar token"));
            });
            failedQueue = [];
            isRefreshing = false;

            localStorage.removeItem(AUTH_TOKEN);
            localStorage.removeItem(REFRESH_TOKEN);
            store.dispatch(signOutSuccess());
            notificationParam.message = "Falha na Autenticação";
            notificationParam.description = "Por favor, faça login novamente";
            notification.error(notificationParam);
            return Promise.reject(error);
          }
        } else {
          isRefreshing = false;
          failedQueue.forEach((prom) => {
            prom.reject(new Error("Refresh token não encontrado"));
          });
          failedQueue = [];
        }
      } catch (refreshError) {
        // Falha na renovação - rejeitar todas as requisições na fila
        failedQueue.forEach((prom) => {
          prom.reject(refreshError);
        });
        failedQueue = [];
        isRefreshing = false;

        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        store.dispatch(signOutSuccess());
        notificationParam.message = "Falha na Autenticação";
        notificationParam.description = "Por favor, faça login novamente";
        notification.error(notificationParam);
        return Promise.reject(refreshError);
      }
    }

    if (unauthorizedCode.includes(error.response?.status)) {
      notificationParam.message = "Falha na Autenticação";
      notificationParam.description = "Por favor, faça login novamente";
      localStorage.removeItem(AUTH_TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);

      store.dispatch(signOutSuccess());
    }

    if (error.response?.status === 404) {
      notificationParam.message = "Não Encontrado";
      if (error.response.data.message) {
        notificationParam.description = error.response.data.message;
      } else if (error.response.data.mensagem) {
        notificationParam.description = error.response.data.mensagem;
      }
    }

    if (error.response?.status === 500) {
      if (error.response.config.method === "delete") {
        notificationParam.message = "Não foi possível excluir";
        notificationParam.description =
          "Este item não pode ser excluído pois está sendo utilizado.";
      } else {
        notificationParam.message = "Erro Interno do Servidor";
      }
    }

    if (error.response?.status === 502) {
      notificationParam.message = "Erro Interno do Servidor";
    }

    if (error.response?.status === 504) {
      notificationParam.message = "Tempo Limite Excedido";
    }

    if (error.response?.status === 508) {
      notificationParam.message = "Tempo Limite de Requisição Excedido";
    }

    if (error.response?.status === 400) {
      notificationParam.message = "Falha na Requisição";
      if (error.response.data.detail) {
        notificationParam.description = error.response.data.detail;
      } else if (error.response.data.message) {
        notificationParam.description = error.response.data.message;
      } else if (error?.response?.data?.cpf) {
        notificationParam.description = "CPF já utilizado por outro usuário";
      } else if (error?.response?.data?.email) {
        notificationParam.description = "E-mail já utilizado por outro usuário";
      } else if (error?.response?.data instanceof Blob) {
        const errorText = await error.response.data.text();

        try {
          const errorJson = JSON.parse(errorText);
          notificationParam.description =
            errorJson.detail || errorJson.message || "";
        } catch (error) {
          console.error(error);
        }
      }
    }

    if (error.response?.status === 429) {
      notification.mensagem = "Limite de uso temporariamente excedido.";
      notificationParam.description = "Por favor, aguarde alguns instantes antes de realizar uma nova tentativa.";
    }

    if (!notificationParam.message) {
      if (error.response?.data?.title) {
        notificationParam.message = error.response.data.title;
      } else {
        notificationParam.message = "Falha na Requisição";
      }

      if (error.response?.data?.message) {
        notificationParam.description = error.response.data.message;
      } else {
        notificationParam.description = "Por favor, tente novamente.";
      }
    }

    notification.error(notificationParam);

    return Promise.reject(error);
  }
);

export default service;
